import { useId, useMemo, useState } from "react";
import { useFormatters, useTranslation } from "@ferrivia/i18n";
import { Text } from "@ferrivia/ui";
import { useTracker } from "@ferrivia/auth-web";
import { ChevronDown } from "lucide-react";
import {
  LabTrendChart,
  PvkAccordion,
  ResultRow,
  ResultSlip,
  trendAxis,
  useAnalyteCopy,
  useRangeCopy,
  type LabTrendSeries,
  type PvkAnalyteRow,
} from "@ferrivia/lab-ui";
import {
  PVK_ANALYTE_ORDER,
  type LabAnalyteCode,
  type LabResult,
  type ReadLabResultAnalyte,
} from "@ferrivia/api-contract";
import { LabReportFiles } from "./LabReportFiles";
import { useCustomerLabs } from "../../../../hooks/useCustomerLabs";
import s from "./LabsPane.module.css";

export interface LabsPaneProps {
  customerId: string;
}

const PVK_CODES: ReadonlySet<string> = new Set(PVK_ANALYTE_ORDER);

/* The three analytes the trend charts, in panel order, each pinned to a
   chart-series token. The list is fixed rather than derived from whatever a
   draw happens to carry: which measurements a clinician is shown over time is
   a clinical-content decision, not something the data should silently change
   draw by draw. The full blood count is deliberately not charted — its
   sub-measurements are on file and shown as readings, and Ville's call was
   to keep the trend to these three.

   Haemoglobin leads (Ville, 2026-09-07), the same reading order the client
   PWA's Results tab uses. `seriesIndex` is not renumbered with the order:
   it pins a colour token to an analyte, so ferritin stays series 1 on both
   surfaces however the panels are stacked. */
const TREND_CODES = [
  { code: "hemoglobin", seriesIndex: 3 },
  { code: "ferritin", seriesIndex: 1 },
  { code: "crp", seriesIndex: 2 },
] as const satisfies ReadonlyArray<{ code: LabAnalyteCode; seriesIndex: 1 | 2 | 3 }>;

// collectedOn is always "YYYY-MM-DD" with no time component, so it is
// formatted in UTC — the same reasoning as the client Results screen's
// formatCollectedOn, which a browser west of Finland would otherwise show a
// day early.
function formatCollectedOn(collectedOn: string, tag: string): string {
  return new Intl.DateTimeFormat(tag, { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" }).format(
    new Date(`${collectedOn}T00:00:00Z`),
  );
}

function formatAxisDate(collectedOn: string, tag: string): string {
  return new Intl.DateTimeFormat(tag, { day: "numeric", month: "short", timeZone: "UTC" }).format(
    new Date(`${collectedOn}T00:00:00Z`),
  );
}

// labCode, not provenance: it is set exactly when a lab actually processed
// the draw, and it is what decides whether a range may carry a lab's name.
// See rangeCopy.ts in @ferrivia/lab-ui.
function labProcessed(draw: LabResult): boolean {
  return draw.labCode !== null;
}

/* Where a draw's numbers came from. The clinic is the one surface that may
   name the laboratory: to a clinician the assay's source is material, since
   a reference range belongs to the lab that reported it. Client surfaces say
   only whether the customer entered it themselves. */
function Provenance({ draw }: { draw: LabResult }) {
  const { t } = useTranslation("clinic");
  const processed = labProcessed(draw);

  return (
    <>
      <span className={processed ? s.provChip : `${s.provChip} ${s.provChipSelfReported}`}>
        <span className={s.provGlyph} aria-hidden="true" />
        {processed ? t("labsPane.provenance.lab", "Arvoterveys") : t("labsPane.provenance.selfReported", "Self-reported")}
      </span>
      {!processed && draw.sourceLab && (
        <Text as="span" size="xs" className={s.sourceLab}>
          {draw.sourceLab}
        </Text>
      )}
    </>
  );
}

function SelfReportedNote({ draw }: { draw: LabResult }) {
  const { t } = useTranslation("clinic");

  if (labProcessed(draw)) {
    return null;
  }
  return (
    <Text as="p" size="xs" className={s.note}>
      {t(
        "labsPane.selfReportedNote",
        "Entered by the customer from a report outside Ferrivia. No lab verified these values, so the reference is shown unattributed.",
      )}
    </Text>
  );
}

/* One draw's readings, as the rows of a slip. A fragment rather than a
   wrapper, because the hairline between two readings is drawn by whatever
   contains them (ResultSlip's `.rows > * + *`) — a div here would collapse
   the whole draw into one child and take those rules with it. */
function DrawReadings({ draw }: { draw: LabResult }) {
  const { labelFor } = useAnalyteCopy();
  const { lineFor } = useRangeCopy({ attributeLab: true });

  const mainAnalytes = draw.analytes.filter((a) => !PVK_CODES.has(a.analyteCode));
  const pvkAnalytes: ReadLabResultAnalyte[] = PVK_ANALYTE_ORDER.map((code: LabAnalyteCode) =>
    draw.analytes.find((a) => a.analyteCode === code),
  ).filter((a): a is ReadLabResultAnalyte => a !== undefined);
  const pvkRows: PvkAnalyteRow[] = pvkAnalytes.map((a) => ({
    analyteCode: a.analyteCode,
    label: labelFor(a.analyteCode),
    value: a.value,
    unit: a.unit,
    rangeText: lineFor(a.refText, draw.labCode),
  }));

  return (
    <>
      {mainAnalytes.map((analyte) => (
        <ResultRow
          key={analyte.analyteCode}
          label={labelFor(analyte.analyteCode)}
          value={analyte.value}
          unit={analyte.unit}
          rangeText={lineFor(analyte.refText, draw.labCode)}
        />
      ))}

      {/* The blood count folds down inside the same list, exactly as it
          does for a client. No `onRowOpened`: the explainer behind a
          client's reading is plain-language copy written for them, and a
          clinician has no use for it — so the rows here render untappable
          rather than as buttons that do nothing. */}
      <PvkAccordion rows={pvkRows} />
    </>
  );
}

function LatestDraw({ draw }: { draw: LabResult }) {
  const { t } = useTranslation("clinic");
  const { tag } = useFormatters();

  return (
    <section className={s.section} aria-labelledby="labs-latest-heading">
      <Text as="h2" id="labs-latest-heading" size="sm" weight="bold" className={s.heading}>
        {t("labsPane.latestDraw", "Latest draw")}
      </Text>

      {/* One slip per draw, the same object the client's Results tab shows
          them — so a clinician and a customer talking about a number are
          looking at the same thing laid out the same way. The date and the
          provenance are its head rather than a metadata row above it.

          Provenance is the one part of the head that differs between the two
          surfaces, and it differs because it must: the client app has no
          reason to name a laboratory, and a clinician does — a reference
          range belongs to the lab that reported it. */}
      <ResultSlip when={formatCollectedOn(draw.collectedOn, tag)} provenance={<Provenance draw={draw} />}>
        <DrawReadings draw={draw} />
      </ResultSlip>

      <SelfReportedNote draw={draw} />
    </section>
  );
}

function Trend({ results }: { results: LabResult[] }) {
  const { t } = useTranslation("clinic");
  const { tag } = useFormatters();
  const { labelFor } = useAnalyteCopy();

  // Oldest first for the chart; `results` arrives newest first.
  const draws = useMemo(() => [...results].reverse(), [results]);
  const dates = useMemo(() => draws.map((draw) => draw.collectedOn), [draws]);

  const series: LabTrendSeries[] = useMemo(
    () =>
      TREND_CODES.flatMap(({ code, seriesIndex }) => {
        const points = draws.flatMap((draw) => {
          const analyte = draw.analytes.find((a) => a.analyteCode === code);
          return analyte
            ? [{ collectedOn: draw.collectedOn, value: analyte.value, selfReported: draw.labCode === null }]
            : [];
        });
        if (points.length === 0) {
          return [];
        }
        const units = new Set(draws.flatMap((d) => d.analytes.filter((a) => a.analyteCode === code).map((a) => a.unit)));
        // Values in different units share no axis, and we never convert them.
        if (units.size > 1) {
          return [];
        }
        const unit = [...units][0] ?? "";
        return [
          {
            analyteCode: code,
            label: labelFor(code),
            unit,
            seriesIndex,
            points,
            // The axis comes from this analyte's own measurements and nothing
            // else — never from its reference range, which would make the
            // scale an implicit comparison against it.
            axis: trendAxis(points.map((p) => p.value)),
          },
        ];
      }),
    [draws, labelFor],
  );

  if (dates.length < 2 || series.length === 0) {
    return null;
  }

  return (
    <section className={s.section} aria-labelledby="labs-trend-heading">
      <div className={s.sectionHead}>
        <Text as="h2" id="labs-trend-heading" size="sm" weight="bold" className={s.heading}>
          {t("labsPane.trend", "Trend")}
        </Text>
        <Text as="span" size="xs" className={s.drawCount}>
          {t("labsPane.drawCount", { count: dates.length, defaultValue: "{{count}} draws" })}
        </Text>
      </div>
      <LabTrendChart
        series={series}
        dates={dates}
        formatDate={(collectedOn) => formatAxisDate(collectedOn, tag)}
      />
    </section>
  );
}

interface EarlierDrawProps {
  draw: LabResult;
  expanded: boolean;
  onToggle: () => void;
}

/* An earlier draw, folded down to its slip head.
 *
 * The head is the disclosure control, which is why this is not a ResultSlip
 * with a hidden body: collapsed, an earlier draw *is* a slip head — the date
 * and where the numbers came from — and expanding it unfolds the same rows
 * the latest draw shows. Same date, same provenance, same readings; only the
 * head is a button, and the styles for it mirror ResultSlip's own.
 *
 * The trailing chevron carries the state, not the label: a control whose
 * accessible name rewrites itself ("show" / "hide") moves under anyone
 * navigating by name, the same reasoning PvkAccordion's fixed count follows. */
function EarlierDraw({ draw, expanded, onToggle }: EarlierDrawProps) {
  const { tag } = useFormatters();
  const bodyId = useId();

  return (
    <div className={s.earlier} data-expanded={expanded}>
      <button
        type="button"
        className={s.earlierHead}
        aria-expanded={expanded}
        aria-controls={bodyId}
        onClick={onToggle}
      >
        <span className={s.earlierSummary}>
          <Text as="span" size="xs" weight="medium" className={s.earlierWhen}>
            {formatCollectedOn(draw.collectedOn, tag)}
          </Text>
          <Provenance draw={draw} />
        </span>
        <ChevronDown className={s.chevron} size={16} strokeWidth={2} aria-hidden />
      </button>

      {/* The panel itself is always in the DOM so `aria-controls` always names
          an element that exists; the readings inside it are mounted only when
          it is open, so a customer with a dozen draws on file does not have
          every one of them rendered behind a closed head. */}
      <div id={bodyId} className={s.earlierBody} hidden={!expanded}>
        {expanded && (
          <>
            <div className={s.earlierRows}>
              <DrawReadings draw={draw} />
            </div>
            <SelfReportedNote draw={draw} />
          </>
        )}
      </div>
    </div>
  );
}

/* Every draw before the latest one, newest first.
 *
 * Folded shut by default because this pane is 320px wide next to a
 * conversation: a clinician opens the draw they want to compare against,
 * rather than scrolling past every number the customer has ever had. What is
 * *not* conditional is that the draws are here at all — the Labs tab used to
 * end at the latest slip and the chart, which left an earlier draw's actual
 * readings unreachable from the console.
 *
 * Opening one is a UI interaction, not a second disclosure: the whole history
 * already reached the browser under the single `patient_results` read this
 * pane logs when it opens, exactly as NotesPane's revision dropdown does. */
function EarlierDraws({ draws, patientId }: { draws: LabResult[]; patientId: string }) {
  const { t } = useTranslation("clinic");
  const tracker = useTracker();
  const [expandedIds, setExpandedIds] = useState<ReadonlySet<string>>(new Set());

  if (draws.length === 0) {
    return null;
  }

  // Several may be open at once — comparing this draw against that one is the
  // reason to open them at all, and an accordion that shuts the previous draw
  // makes exactly that impossible.
  function toggle(draw: LabResult, index: number) {
    const opening = !expandedIds.has(draw.id);
    const next = new Set(expandedIds);
    if (opening) {
      next.add(draw.id);
    } else {
      next.delete(draw.id);
    }
    setExpandedIds(next);
    if (opening) {
      tracker.track("clinic.patient_earlier_draw_opened", { patient_id: patientId, draw_index: index });
    }
  }

  return (
    <section className={s.section} aria-labelledby="labs-earlier-heading">
      <div className={s.sectionHead}>
        <Text as="h2" id="labs-earlier-heading" size="sm" weight="bold" className={s.heading}>
          {t("labsPane.earlierDraws", "Earlier draws")}
        </Text>
        <Text as="span" size="xs" className={s.drawCount}>
          {t("labsPane.drawCount", { count: draws.length, defaultValue: "{{count}} draws" })}
        </Text>
      </div>
      <div className={s.earlierList}>
        {draws.map((draw, index) => (
          <EarlierDraw
            key={draw.id}
            draw={draw}
            expanded={expandedIds.has(draw.id)}
            onToggle={() => toggle(draw, index)}
          />
        ))}
      </div>
    </section>
  );
}

/* The clinic console's Labs tab.
 *
 * Readings come from @ferrivia/lab-ui, the same components the client PWA's
 * Results tab renders — one measurement, worded and laid out the same way on
 * both sides of the conversation, so a clinician and a customer discussing a
 * number are looking at the same thing.
 *
 * Nothing on this pane derives, scores, thresholds, flags, colour-codes or
 * ranks a value. That rule binds a clinician-facing surface exactly as it
 * binds a client-facing one (root CLAUDE.md, "The software does not
 * interpret" — "It applies to every surface"). The trend is permitted as
 * display-only charting (compliance/roadmap.md, REQ-MDR-002): it plots
 * measured numbers against a printed scale, and does nothing else with them.
 * The draws below it are ordered by when the sample was taken and by nothing
 * else — no draw is promoted, grouped or marked on account of what it
 * measured.
 */
export function LabsPane({ customerId }: LabsPaneProps) {
  const { t } = useTranslation("clinic");
  const state = useCustomerLabs(customerId);

  if (state.status === "loading") {
    return (
      <Text as="p" size="sm" className={s.helper}>
        {t("labsPane.loading", "Loading lab results…")}
      </Text>
    );
  }

  if (state.status === "error") {
    return (
      <Text as="p" size="sm" className={s.helper}>
        {t("labsPane.error", "Couldn't load lab results.")}
      </Text>
    );
  }

  const [latest, ...earlier] = state.results;
  if (!latest) {
    return (
      <div className={s.root}>
        <div className={s.emptyBox}>
          <Text as="p" size="sm" weight="medium">
            {t("labsPane.empty.title", "No results on file")}
          </Text>
          <Text as="p" size="sm">
            {t("labsPane.empty.body", "Nothing has been recorded for this customer yet.")}
          </Text>
        </div>
        <LabReportFiles customerId={customerId} />
      </div>
    );
  }

  return (
    <div className={s.root}>
      <LatestDraw draw={latest} />
      {/* Below two draws there is no Trend section at all — not an empty
          chart or a "not enough data" placeholder taking up a third of a
          320px pane. */}
      <Trend results={state.results} />
      {/* Below the charts, which is where Ville asked for them: the figure
          is the shape of the history, and these are the numbers behind it. */}
      <EarlierDraws draws={earlier} patientId={customerId} />
      <LabReportFiles customerId={customerId} />
    </div>
  );
}
